import { TimelineViewModelData, TimelineViewRenderingData } from '../types';
import { ArdoqId } from '@ardoq/api-types';
import { filterOutCollapsedHierarchy } from './filterOutCollapsedHierarchy';
import { applyInteractiveStates } from './applyInteractiveStates';
import { applyCollapsedFieldDataStates } from './applyCollapsedFieldDataStates';
import {
  clampRowsToVirtualWindow,
  getContainerHeight,
  getVirtualScrollState,
} from '../calculations';
import { getTransitLinksById } from '../items/calculateTransitLinks';

type GetFilteredRenderingDataWithInteractiveNodeStatesArgs = {
  timelineViewRenderingData: Pick<
    TimelineViewRenderingData,
    | 'collapsedGroupIds'
    | 'allItemsWithRenderingData'
    | 'collapsedFieldDataIds'
    | 'timelineChartRenderingData'
    | 'backgroundScrollHeight'
    | 'virtualScrollState'
  >;
  hoveredItemId: string | null;
  focusedItemId: ArdoqId | null;
  timelineViewModelData: TimelineViewModelData;
};

/**
 * Takes already composed rendering data and filters out the collapsed hierarchy.
 *
 * It calls getTimelinePhasesWithHighlightBackground to set the highlight color
 * backgrounds on phases.
 *
 * Note that it only returns properties it is responsible for, and is not bound to a certain state structure.
 */
export const getFilteredRenderingDataWithInteractiveNodeStates = ({
  timelineViewRenderingData: {
    collapsedGroupIds,
    collapsedFieldDataIds,
    allItemsWithRenderingData,
    timelineChartRenderingData,
    backgroundScrollHeight: previousBackgroundScrollHeight,
    virtualScrollState: previousVirtualScrollState,
  },
  timelineViewModelData,
  hoveredItemId,
  focusedItemId,
}: GetFilteredRenderingDataWithInteractiveNodeStatesArgs) => {
  const {
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy,
    itemPhasesWithRenderingDataWithoutCollapsedHierarchy,
  } = filterOutCollapsedHierarchy({
    timelineViewRenderingData: {
      collapsedGroupIds,
      allItemsWithRenderingData,
    },
    timelineViewModelData,
  });

  /**
   * A "transit link" is a line connecting a parent to its child that passes through
   * the current row.
   */
  const transitLinksById = getTransitLinksById(
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy
  );

  const {
    itemLabelsWithCollapsedFieldDataState,
    itemPhasesWithCollapsedFieldDataState,
    allItemTopsWithSentinel,
    renderedPhasesByGraphNodeId,
  } = applyCollapsedFieldDataStates({
    timelineViewRenderingData: {
      itemLabelsWithRenderingDataWithoutCollapsedHierarchy,
      itemPhasesWithRenderingDataWithoutCollapsedHierarchy,
      collapsedFieldDataIds,
    },
    timelineViewModelData: {
      collapsibleGroups: timelineViewModelData.collapsibleGroups,
    },
  });

  /**
   * When user collapses field on the bottom of the scroll container, the view jumps due to
   * adjusted container height. To avoid that, we track the current visible area height.
   *
   * If it is increased, we don't care. If it is reduced, we don't change it unless the
   * empty space is larger than the visible area's height.
   *
   * There is no official requirement for this, it simply makes sense to me.
   */

  const { backgroundScrollHeight, visibleAreaHeight } = getContainerHeight({
    allItemTopsWithSentinel,
    timelineChartRenderingData,
    backgroundScrollHeight: previousBackgroundScrollHeight,
  });

  const nextVirtualScrollState = getVirtualScrollState({
    chartHeight: visibleAreaHeight,
    scrollTop: previousVirtualScrollState.scrollTop,
    currentIndex: previousVirtualScrollState.firstItemIndex,
    itemTops: allItemTopsWithSentinel,
  });

  const {
    labelsInVirtualWindow,
    phasesInVirtualWindow,
    itemTopsInVirtualWindow,
  } = clampRowsToVirtualWindow({
    allItemTopsWithSentinel,
    allLabels: itemLabelsWithCollapsedFieldDataState,
    allPhases: itemPhasesWithCollapsedFieldDataState,
    virtualScrollState: nextVirtualScrollState,
  });

  const { renderedItemLabels, renderedItemPhases } = applyInteractiveStates({
    timelineViewRenderingData: {
      // this is where they need to go
      labelsInVirtualWindow,
      phasesInVirtualWindow,
    },
    timelineViewModelData,
    hoveredItemId,
    focusedItemId,
  });

  return {
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy,
    itemPhasesWithRenderingDataWithoutCollapsedHierarchy,
    itemLabelsWithCollapsedFieldDataState,
    itemPhasesWithCollapsedFieldDataState,
    labelsInVirtualWindow,
    phasesInVirtualWindow,
    renderedItemLabels,
    renderedItemPhases,
    renderedPhasesByGraphNodeId,

    collapsedGroupIds,
    collapsedFieldDataIds,

    allItemTopsWithSentinel,
    itemTopsInVirtualWindow,
    virtualScrollState: nextVirtualScrollState,
    backgroundScrollHeight,
    transitLinksById,
  };
};
